import { faClose, faRobot, faSpinner, faTrash } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { ButtonIcon } from "../../../components/Button/Button"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import THEME from "../../../configuration/Theme"
import { SIZE } from "../../../configuration/Sizes"
import type { IWhatsAppAgentOverviewDto } from "../../../Objects/WhatsAppAutomation/WhatsAppAgentOverview"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"

type Props = {
  agent: IWhatsAppAgentOverviewDto
  onRevoke: () => Promise<void>
  onClose: () => void
}

export const PlatformWhatsAppAgentPopup = ({ agent, onRevoke, onClose }: Props) => {
  const { t } = useTranslation("platformWhatsApp")

  const [isRevoking, setIsRevoking] = useState(false)

  const revokeAgent = async () => {
    if (isRevoking) return

    setIsRevoking(true)

    try {
      await onRevoke()
    } catch {
      return
    } finally {
      setIsRevoking(false)
    }
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert}>
            <TitlePageResponsiveIconSmall
              title={t("agent.managementTitle")}
              icon={faRobot}
              iconColor={THEME.colors.iconBGColor}
            >
              <ButtonIcon
                sizeNumber={SIZE.buttonCloseIconSize}
                bgColor={THEME.colors.buttonBGCloseColor}
                iconColor={THEME.colors.buttonFGCloseColor}
                icon={faClose}
                onClick={onClose}
                size="small"
                descriptionKey="interactionDescriptions:pages.adminDashboard.platformWhatsApp.platformWhatsAppAgentPopup.button.onClose"
              />
            </TitlePageResponsiveIconSmall>

            <div className="grid max-h-[75dvh] gap-4 overflow-x-hidden overflow-y-auto p-4">
              <Animate type={AnimationTypes.fadeInTraslate} duration="0.5s">
                <div className="rounded-2xl border-2 border-orange-200 bg-gradient-to-br from-white via-orange-50/40 to-white p-4 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="truncate text-lg font-black text-slate-900" title={agent.name}>
                        {agent.name}
                      </h3>

                      <p className="mt-1 text-sm font-semibold text-slate-500">
                        {agent.connectedPhone || t("agent.noPhone")}
                      </p>
                    </div>

                    <span
                      className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold ${agent.isWhatsAppReady ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}
                    >
                      {agent.isWhatsAppReady ? t("ready") : t("agent.waiting")}
                    </span>
                  </div>
                </div>
              </Animate>

              {agent.currentQrCodeDataUrl && (
                <Animate type={AnimationTypes.fadeInTraslate} duration="0.65s">
                  <div className="rounded-2xl border border-sky-200 bg-sky-50 p-4 text-center">
                    <p className="mb-3 text-sm font-bold text-sky-900">{t("agent.scanQr")}</p>

                    <img
                      src={agent.currentQrCodeDataUrl}
                      alt={t("agent.qrAlt")}
                      className="mx-auto h-52 w-52 rounded-xl border border-sky-200 bg-white object-contain p-2 shadow-sm"
                    />
                  </div>
                </Animate>
              )}

              <Animate type={AnimationTypes.fadeInTraslate} duration="0.8s">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("agent.health")}</p>

                  <p className="mt-2 text-sm font-semibold leading-6 text-slate-700">{agent.healthDescription}</p>

                  {agent.lastError && (
                    <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-3 text-xs font-semibold text-red-700">
                      <strong>{t("agent.lastError")}:</strong> {agent.lastError}
                    </div>
                  )}
                </div>
              </Animate>

              {!agent.hasPendingPairing && (
                <Animate type={AnimationTypes.fadeInTraslate} duration="0.95s">
                  <div className="grid gap-2 sm:grid-cols-2">
                    <div className="min-w-0 rounded-xl border border-slate-200 bg-white p-3 text-xs text-slate-600">
                      <strong className="block text-slate-800">{t("agent.machine")}</strong>

                      <span className="mt-1 block break-all">{agent.machineId}</span>
                    </div>

                    <div className="rounded-xl border border-slate-200 bg-white p-3 text-xs text-slate-600">
                      <strong className="block text-slate-800">{t("agent.version")}</strong>

                      <span className="mt-1 block">{agent.version}</span>
                    </div>
                  </div>
                </Animate>
              )}

              <Animate type={AnimationTypes.fadeInTraslate} duration="1.1s">
                <button
                  type="button"
                  disabled={isRevoking}
                  aria-busy={isRevoking}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 font-bold text-red-700 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-red-100 hover:shadow-md disabled:cursor-wait disabled:opacity-60"
                  onClick={revokeAgent}
                  data-interaction-description-key="interactionDescriptions:pages.adminDashboard.platformWhatsApp.platformWhatsAppAgentPopup.button.revokeAgent"
                >
                  <FontAwesomeIcon icon={isRevoking ? faSpinner : faTrash} spin={isRevoking} />
                  {isRevoking ? t("agent.revoking") : t("agent.revoke")}
                </button>
              </Animate>
            </div>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
